import { useState, useEffect, useCallback } from "react";
import { upsertUserAssignments, type UpsertAssignmentInput } from "../../../api/accessManagement/assignmentsService";

export interface AssignRoleUser {
  id?: string;
  userId?: string;
  _id?: string;
  name: string;
  email: string;
  roles: string[];
  scopeType?: string;
  scopeId?: string;
  assignments?: {
    assignmentId?: string;
    roleCode: string;
    scopeType?: string;
    scopeIds?: string[];
  }[];
}

interface UseAssignRoleModalParams {
  show: boolean;
  onHide: () => void;
  user?: AssignRoleUser | null;
  onSuccess?: () => void;
}

export function useAssignRoleModal({
  show,
  onHide,
  user,
  onSuccess,
}: UseAssignRoleModalParams) {
  type AssignmentForm = {
    assignmentId?: string;
    roleCode: string;
    scopeType: string;
    scopeIds: string[];
    academyId?: string;
  };

  const [assignments, setAssignments] = useState<AssignmentForm[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (show && user) {
      setSubmitError(null);
      if (Array.isArray(user.assignments) && user.assignments.length > 0) {
        setAssignments(
          user.assignments.map((a) => ({
            assignmentId: a.assignmentId,
            roleCode: a.roleCode,
            scopeType: a.scopeType ?? "",
            scopeIds: a.scopeIds ?? [],
            academyId: undefined,
          }))
        );
      } else {
        setAssignments([
          {
            roleCode: user.roles[0] ?? "VENUE_ADMIN",
            scopeType: user.scopeType ?? "",
            scopeIds: user.scopeId ? [user.scopeId] : [],
            academyId: undefined,
          },
        ]);
      }
    } else if (show) {
      setSubmitError(null);
      setAssignments([
        {
          roleCode: "VENUE_ADMIN",
          scopeType: "",
          scopeIds: [],
          academyId: undefined,
        },
      ]);
    }
  }, [show, user]);

  const updateAssignment = useCallback(
    (index: number, patch: Partial<AssignmentForm>) => {
      setAssignments((prev) =>
        prev.map((a, i) => (i === index ? { ...a, ...patch } : a))
      );
    },
    []
  );

  const handleRoleChange = useCallback(
    (index: number, value: string) => {
      updateAssignment(index, { roleCode: value });
    },
    [updateAssignment]
  );

  const handleScopeTypeChange = useCallback(
    (index: number, value: string) => {
      updateAssignment(index, {
        scopeType: value,
        scopeIds: [],
        academyId: undefined,
      });
    },
    [updateAssignment]
  );

  const handleScopeIdsChange = useCallback(
    (index: number, values: string[]) => {
      updateAssignment(index, { scopeIds: values });
    },
    [updateAssignment]
  );

  const handleAcademyIdChange = useCallback(
    (index: number, value: string) => {
      updateAssignment(index, { academyId: value, scopeIds: [] });
    },
    [updateAssignment]
  );

  const handleAddAssignment = useCallback(() => {
    setAssignments((prev) => [
      ...prev,
      { roleCode: "", scopeType: "", scopeIds: [], academyId: undefined },
    ]);
  }, []);

  const handleRemoveAssignment = useCallback((index: number) => {
    setAssignments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!user) {
      setSubmitError("No user selected");
      return;
    }
    const userId = user.id || user._id || user.userId;
    if (!userId) {
      setSubmitError("User id is missing");
      return;
    }
    // Validate that we have at least one assignment and every assignment has a role.
    if (!assignments.length) {
      setSubmitError("Please add at least one role assignment.");
      return;
    }
    if (assignments.some((a) => !a.roleCode)) {
      setSubmitError("Please select a role for each assignment.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const payloadAssignments: UpsertAssignmentInput[] = assignments.map((a) => ({
        assignment_id: a.assignmentId,
        role_code: a.roleCode,
        scope_type: a.scopeType,
        scope_ids: a.scopeIds,
      }));

      await upsertUserAssignments(userId, payloadAssignments);
      onSuccess?.();
      onHide();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to assign role"
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [user, assignments, onHide, onSuccess]);

  return {
    functions: {
      handleRoleChange,
      handleScopeTypeChange,
      handleScopeIdsChange,
      handleAcademyIdChange,
      handleAddAssignment,
      handleRemoveAssignment,
      handleSubmit,
    },
    values: {
      assignments,
      submitError,
    },
    loading: {
      isSubmitting,
    },
  };
}
